import { Loader } from '@zoom/components';
import classnames from 'classnames';
import { addHours, differenceInMinutes, subHours } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GanttChart } from './GanttChart';
import { RealTimeAdherenceBottomPanelHeader } from './RealTimeAdherenceBottomPanelHeader';
import {
  ActivityElementsGanttData,
  GanttChartScheduleElement,
  OutOfAdherenceElementsGanttData,
  RealTimeAdherenceBottomPanelProps,
} from './types';
import {
  mapActivityElementsGanttData,
  mapOutOfAdherenceElementsGanttData,
  mapScheduleElementsGanttData,
} from './utils';
import { useAuth } from '../../app/AuthorizationProvider';
import { BottomPanel, BottomPanelBody } from '../../components/layout/BottomPanel';
import {
  AdherenceEvent,
  ScheduleItem,
  useGetAgentProductionScheduleWithoutStatsQuery,
  useGetRealTimeAdherenceGanttChartInitialDataQuery,
  useGetScheduleElementsQuery,
  useRealTimeAdherenceEventsChangedSubscription,
  useRealTimeAgentEventsChangedSubscription,
  useRealTimeScheduleChangedSubscription,
} from '../../graphql/components';
import { recordBy } from '../../utils/array';
import { GanttChartYAxisLabels } from '../AdherenceHistory/GanttChartYAxisLabels';
import { MIN_15 } from '../Scheduling/config';

export const RealTimeAdherenceBottomPanel: React.FC<RealTimeAdherenceBottomPanelProps> = ({
  agentOverviewItem,
  onClose,
  timeStamp,
  teams,
}) => {
  const { t } = useTranslation();
  const { timezone } = useAuth();

  const currentQuarterHour = new Date(timeStamp - (new Date(timeStamp).getTime() % MIN_15));
  const { agentId } = agentOverviewItem;

  const [isComponentInitialized, setComponentInitialized] = useState(false);
  const [ganttStartTime, setGanttStartTime] = useState(subHours(currentQuarterHour, 13).getTime());
  const [transformedScheduleElements, setTransformedScheduleElements] = useState<GanttChartScheduleElement[]>([]);
  const [transformedActivityElements, setTransformedActivityElements] = useState<ActivityElementsGanttData>({
    elements: [],
  });
  const [transformedOutOfAdherenceElements, setTransformedOutOfAdherenceElements] =
    useState<OutOfAdherenceElementsGanttData>({
      elements: [],
    });

  const ganttEndTime = useMemo(() => addHours(ganttStartTime, 26).getTime(), [ganttStartTime]);

  const [scheduleElementsDateRange, setScheduleElementsDateRange] = useState({
    endDateTime: addHours(ganttEndTime, 2).getTime(),
    startDateTime: ganttStartTime,
    shouldCleanUpOldElements: true,
  });

  useEffect(() => {
    // prevents onCompleted adherence initial data query's handler from being executed twice on first load
    if (isComponentInitialized) {
      setScheduleElementsDateRange({
        endDateTime: addHours(ganttEndTime, 2).getTime(),
        startDateTime: ganttStartTime,
        shouldCleanUpOldElements: true, // makes sure old schedule elements for previous agent would be cleaned up when new data are handled
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  useEffect(() => {
    setComponentInitialized(true);
  }, []);

  useEffect(() => {
    const { inProgressAgentActivityIndex } = transformedActivityElements;
    const { inProgressOOAEventIndex } = transformedOutOfAdherenceElements;

    if (inProgressAgentActivityIndex !== undefined) {
      setTransformedActivityElements((state) => ({
        ...state,
        elements: [
          ...transformedActivityElements.elements.slice(0, inProgressAgentActivityIndex),
          {
            ...transformedActivityElements.elements[inProgressAgentActivityIndex],
            end: timeStamp,
          },
          ...transformedActivityElements.elements.slice(inProgressAgentActivityIndex + 1),
        ],
      }));
    }

    if (inProgressOOAEventIndex !== undefined) {
      setTransformedOutOfAdherenceElements((state) => ({
        ...state,
        elements: [
          ...transformedOutOfAdherenceElements.elements.slice(0, inProgressOOAEventIndex),
          {
            ...transformedOutOfAdherenceElements.elements[inProgressOOAEventIndex],
            end: timeStamp,
          },
          ...transformedOutOfAdherenceElements.elements.slice(inProgressOOAEventIndex + 1),
        ],
      }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeStamp]);

  const { data: scheduleElementsData, loading: isScheduleElementsDataLoading } = useGetScheduleElementsQuery();

  const scheduleElementsRecord = useMemo(
    () => recordBy(scheduleElementsData?.scheduleElements ?? [], 'id'),
    [scheduleElementsData?.scheduleElements]
  );

  const {
    data: ganttChartData,
    loading: isGanttChartDataLoading,
    refetch: refetchRTAGanttChartInitialData,
  } = useGetRealTimeAdherenceGanttChartInitialDataQuery({
    variables: {
      agentId,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    onCompleted: (data) => {
      if (!data) {
        return;
      }

      setTransformedActivityElements(
        mapActivityElementsGanttData(t, timeStamp, data.realTimeAdherenceGanttChartInitialData.agentEvents)
      );
      setTransformedOutOfAdherenceElements(
        mapOutOfAdherenceElementsGanttData(
          t,
          scheduleElementsRecord,
          timeStamp,
          data.realTimeAdherenceGanttChartInitialData.adherenceEvents
        )
      );
    },
    skip: isScheduleElementsDataLoading,
  });

  const {
    data: agentsScheduleData,
    loading: isAgentsScheduleDataLoading,
    refetch: refetchAgentProductionSchedule,
  } = useGetAgentProductionScheduleWithoutStatsQuery({
    variables: {
      parameters: {
        agentIds: [agentId],
        dateTimeRange: {
          endDateTime: new Date(scheduleElementsDateRange.endDateTime).toISOString(),
          startDateTime: new Date(scheduleElementsDateRange.startDateTime).toISOString(),
        },
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    onCompleted: (data) => {
      const newElements = data?.agentProductionScheduleWithoutStats?.scheduleItems ?? [];

      if (scheduleElementsDateRange.shouldCleanUpOldElements) {
        // use only new elements - this will clean up all previous ones...
        setTransformedScheduleElements(
          mapScheduleElementsGanttData(t, scheduleElementsRecord, newElements as ScheduleItem[])
        );
        return;
      }

      // filter out possible duplicate element which should be already present in transformedScheduleElements array
      const filteredElements = newElements.filter(
        ({ dateTimeRange: { startDateTime } }) =>
          new Date(startDateTime).getTime() >= scheduleElementsDateRange.startDateTime
      );

      // filter out previous elements that already got out of viewed range from left side
      const filteredPreviousElements = transformedScheduleElements.filter(
        ({ end }) => end > scheduleElementsDateRange.startDateTime
      );

      if (filteredElements.length) {
        setTransformedScheduleElements([
          ...filteredPreviousElements,
          ...mapScheduleElementsGanttData(t, scheduleElementsRecord, filteredElements as ScheduleItem[]),
        ]);
      }
    },
    skip: isScheduleElementsDataLoading,
  });

  useRealTimeAgentEventsChangedSubscription({
    variables: { agentId },
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData.data?.realTimeAgentEventsChanged) {
        const changedAgentEvents = subscriptionData.data?.realTimeAgentEventsChanged.agentEvents;
        const { elements: previousElements, inProgressAgentActivityIndex } = transformedActivityElements;

        if (
          inProgressAgentActivityIndex !== undefined &&
          changedAgentEvents.find(({ id }) => id === previousElements[inProgressAgentActivityIndex].key)
        ) {
          previousElements.splice(inProgressAgentActivityIndex, 1);
        }

        const newDataTransformed = mapActivityElementsGanttData(t, timeStamp, changedAgentEvents, previousElements);

        setTransformedActivityElements(newDataTransformed);
      }
    },
  });

  useRealTimeAdherenceEventsChangedSubscription({
    variables: { agentId },
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData.data?.realTimeAdherenceEventsChanged) {
        const changedAdherenceEvents = subscriptionData.data?.realTimeAdherenceEventsChanged.adherenceEvents;
        const { elements: previousElements, inProgressOOAEventIndex } = transformedOutOfAdherenceElements;

        if (
          inProgressOOAEventIndex !== undefined &&
          changedAdherenceEvents.find(({ id }) => id === previousElements[inProgressOOAEventIndex].key)
        ) {
          previousElements.splice(inProgressOOAEventIndex, 1);
        }

        const newDataTransformed = mapOutOfAdherenceElementsGanttData(
          t,
          scheduleElementsRecord,
          timeStamp,
          changedAdherenceEvents as AdherenceEvent[],
          previousElements
        );

        setTransformedOutOfAdherenceElements(newDataTransformed);
      }
    },
  });

  useRealTimeScheduleChangedSubscription({
    variables: { agentId },
    onData: ({ data: subscriptionData }) => {
      if (subscriptionData.data?.realTimeScheduleChanged) {
        const { from, to } = subscriptionData.data?.realTimeScheduleChanged ?? {};
        const changeStartTimeStamp = new Date(from).getTime();
        const changeEndTimeStamp = new Date(to).getTime();
        const dateRangeOfChangeOverlapsVisibleRange =
          changeEndTimeStamp > ganttStartTime && changeStartTimeStamp < ganttEndTime;
        const dateRangeOverlapsWithExtraRange =
          changeEndTimeStamp > ganttStartTime && changeStartTimeStamp < scheduleElementsDateRange.endDateTime;

        if (!dateRangeOfChangeOverlapsVisibleRange && !dateRangeOverlapsWithExtraRange) {
          // do not refetch any data as change of production schedule happened outside of visible and extra +2 hours prefetched range
          return;
        }

        if (scheduleElementsDateRange.startDateTime === ganttStartTime) {
          // no timeline shift has happened yet, refetch production schedule manually
          setScheduleElementsDateRange((state) => ({
            ...state,
            shouldCleanUpOldElements: true,
          }));

          refetchAgentProductionSchedule();
        } else {
          // reset parameters for production schedule query, refetch should happen automatically
          setScheduleElementsDateRange((state) => ({
            ...state,
            startDateTime: ganttStartTime,
            shouldCleanUpOldElements: true,
          }));
        }

        if (dateRangeOfChangeOverlapsVisibleRange) {
          // refetch RTA initial data in case elements were recalculated due to change in production schedule in visible range
          refetchRTAGanttChartInitialData();
        }
      }
    },
  });

  useEffect(() => {
    const newStartTime = subHours(currentQuarterHour, 13).getTime();

    if (newStartTime === ganttStartTime) {
      // prevent updating ranges for initial execution of effect
      return;
    }

    setGanttStartTime(newStartTime);

    const newEndTime = addHours(newStartTime, 26);
    if (differenceInMinutes(scheduleElementsDateRange.endDateTime, newEndTime) <= 15) {
      // update date range parameters for agents production schedule query
      setScheduleElementsDateRange((state) => ({
        startDateTime: state.endDateTime,
        endDateTime: addHours(state.endDateTime, 2).getTime(),
        shouldCleanUpOldElements: false,
      }));
    }

    // eslint-disable-next-line
  }, [currentQuarterHour]);

  // content of Gantt chart should not be rendered whenever real activity data and scheduled items are not available
  const isGanttChartDataEmpty =
    !ganttChartData?.realTimeAdherenceGanttChartInitialData?.agentEvents.length &&
    !transformedOutOfAdherenceElements.elements.length &&
    !agentsScheduleData?.agentProductionScheduleWithoutStats?.scheduleItems?.length &&
    !transformedScheduleElements.length;
  const showLoaderForGanttChart =
    isGanttChartDataLoading || isScheduleElementsDataLoading || isAgentsScheduleDataLoading;

  return (
    <BottomPanel>
      <RealTimeAdherenceBottomPanelHeader
        agentData={agentOverviewItem}
        onClose={onClose}
        timeStamp={timeStamp}
        teams={teams}
      />
      <BottomPanelBody className="real-time-adherence__detail-view-container">
        <div
          className="real-time-adherence__gantt-chart"
          data-test-id="real-time-adherence-detail:gantt-chart-container"
        >
          <div
            className={classnames(
              'real-time-adherence__chart-loader',
              'real-time-adherence__chart-loader--gantt-chart',
              {
                'real-time-adherence__chart-loader--active': showLoaderForGanttChart,
              }
            )}
            data-test-id="real-time-adherence-detail:gantt-chart-loader"
          >
            {showLoaderForGanttChart && <Loader size={40} color="#63ad1a" />}
          </div>
          <div className="d-flex align-items-end">
            <GanttChartYAxisLabels
              isHidden={
                ((isGanttChartDataLoading || isScheduleElementsDataLoading) && !ganttChartData) || isGanttChartDataEmpty
              }
              slug="real-time-adherence"
              t={t}
            />
            <GanttChart
              agentId={agentId}
              activityElements={transformedActivityElements.elements}
              endTime={ganttEndTime}
              isLoading={showLoaderForGanttChart}
              isDataEmpty={isGanttChartDataEmpty}
              outOfAdherenceElements={transformedOutOfAdherenceElements.elements}
              sourceScheduleElements={scheduleElementsData?.scheduleElements}
              scheduleElements={transformedScheduleElements}
              startTime={ganttStartTime}
              timezone={timezone}
              timeStamp={timeStamp}
            />
          </div>
        </div>
      </BottomPanelBody>
    </BottomPanel>
  );
};
